import { useState } from "react"

import { useTranslation } from "react-i18next"

import { Grid } from "@mui/material"
import { RadioButton, Typography } from "@synapse-analytics/synapse-ui"

import type { BaseSimpleDialogProps } from "../../../../components/dialogs/BaseSimpleDialog"
import { BaseSimpleDialog } from "../../../../components/dialogs/BaseSimpleDialog"
import type { FilterBranchDeletionOptions } from "../../types/interfaces"

/**
 * Props for the FilterDeletionDialog component.
 */
interface FilterDeletionDialogProps extends BaseSimpleDialogProps {
  /** Array of options to display in the dialog. */
  options: Array<FilterBranchDeletionOptions>

  /** Function to handle the acceptance of the selected option. */
  handleAccept: (selectedOption: FilterBranchDeletionOptions) => FilterBranchDeletionOptions
}

/**
 * A dialog component to select which node to delete, handle different cases when removing a filter node.
 *
 * @param {FilterDeletionDialogProps} props - The props for the FilterDeletionDialog component.
 * @returns A React element (Deletion dialog with options).
 */
export function FilterDeletionDialog(props: Readonly<FilterDeletionDialogProps>): React.ReactElement {
  // Prop destructuring
  const { options, handleAccept } = props

  // Translation hook
  const { t } = useTranslation("workflows", {
    keyPrefix: "filterDeletionDialog",
  })

  // State hooks
  const [selectedOption, setSelectedOption] = useState<FilterBranchDeletionOptions>(options[0] ?? "All nodes")

  return (
    <BaseSimpleDialog disableMainBtn={!selectedOption} {...props} onAccept={() => handleAccept(selectedOption)}>
      <Grid item xs={12} display="flex" flexDirection="column">
        <Typography variant="h3-bold">{t("title")}</Typography>

        <Grid item display="flex" xs={12} mt={1} gap={2}>
          {options?.map((option) => (
            <RadioButton
              key={option}
              label={t(`deletionOptions.${option}`, {
                defaultValue: option,
              })}
              name={option}
              value={option}
              checked={selectedOption === option}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (e.target?.value !== selectedOption) {
                  setSelectedOption(e.target.value as FilterBranchDeletionOptions)
                }
              }}
            />
          ))}
        </Grid>
      </Grid>
    </BaseSimpleDialog>
  )
}
